/**
 * UsersPage — Manage tribe members (primary users only).
 * - View members (fetched from API)
 * - Edit member name and role
 * - Invite via email (member sets own password via link)
 * - Add manually (admin sets temporary password, shares verbally)
 * - Remove members
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useApi } from '../../hooks/useApi';
import {
  UserPlus, Shield, ShieldCheck, Trash2, Send, AlertCircle,
  CheckCircle, Loader, Mail, KeyRound, Info, Pencil, X,
} from 'lucide-react';

interface HouseholdMember {
  userId: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  email: string;
  role: 'primary' | 'member';
  isAccountHolder?: boolean;
  joinedAt?: string;
}

interface HouseholdResponse {
  householdId: string;
  name: string;
  members: HouseholdMember[];
  settings: Record<string, unknown>;
  stores: unknown[];
}

type InviteMethod = 'email' | 'manual';

const UsersPage: React.FC = () => {
  const { user } = useAuth();
  const { get, post, put, del } = useApi();

  // Member list state
  const [members, setMembers] = useState<HouseholdMember[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(true);
  const [membersError, setMembersError] = useState('');

  // Invite form state
  const [inviteMethod, setInviteMethod] = useState<InviteMethod>('email');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'primary' | 'member'>('member');
  const [tempPassword, setTempPassword] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [inviteStatus, setInviteStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [inviteMessage, setInviteMessage] = useState('');

  const [removingUserId, setRemovingUserId] = useState<string | null>(null);

  // Edit member state
  const [editingMember, setEditingMember] = useState<HouseholdMember | null>(null);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editRole, setEditRole] = useState<'primary' | 'member'>('member');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');

  const householdId = user?.householdId;
  const isPrimary = user?.role === 'primary';

  const fetchMembers = useCallback(async () => {
    if (!householdId) return;
    setIsLoadingMembers(true);
    setMembersError('');
    try {
      const data = await get<HouseholdResponse>(`/v1/households/${householdId}`);
      setMembers(data.members || []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load members';
      setMembersError(msg);
    } finally {
      setIsLoadingMembers(false);
    }
  // eslint-disable-next-line
  }, [householdId]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!householdId) return;

    setIsInviting(true);
    setInviteStatus('idle');
    setInviteMessage('');

    try {
      const payload: Record<string, string> = {
        email: inviteEmail,
        firstName,
        lastName,
        displayName: firstName,
        role: inviteRole,
        inviteMethod,
      };

      if (inviteMethod === 'manual') {
        payload.temporaryPassword = tempPassword;
      }

      const result = await post<{ message?: string; displayName?: string }>(
        `/v1/households/${householdId}/invite`,
        payload,
      );

      setInviteStatus('success');
      if (inviteMethod === 'manual') {
        setInviteMessage(result.message || `${firstName} has been added to your tribe.`);
      } else {
        setInviteMessage(`Invitation email sent to ${inviteEmail}`);
      }

      // Reset form
      setFirstName('');
      setLastName('');
      setInviteEmail('');
      setInviteRole('member');
      setTempPassword('');

      // Refresh member list (manual flow adds immediately)
      if (inviteMethod === 'manual') {
        await fetchMembers();
      }
    } catch (err: unknown) {
      setInviteStatus('error');
      const msg = err instanceof Error ? err.message : 'Failed to send invitation';
      setInviteMessage(msg);
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemove = async (member: HouseholdMember) => {
    if (!householdId) return;
    const confirmed = window.confirm(
      `Remove ${member.displayName} (${member.email}) from the tribe?`
    );
    if (!confirmed) return;

    setRemovingUserId(member.userId);
    try {
      await del(`/v1/households/${householdId}/members/${member.userId}`);
      await fetchMembers();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to remove member';
      alert(msg);
    } finally {
      setRemovingUserId(null);
    }
  };

  // --- Edit member handlers ---

  const openEdit = (member: HouseholdMember) => {
    setEditingMember(member);
    setEditDisplayName(member.displayName || '');
    setEditFirstName(member.firstName || '');
    setEditLastName(member.lastName || '');
    setEditRole(member.role);
    setEditError('');
  };

  const closeEdit = () => {
    setEditingMember(null);
    setEditError('');
  };

  const handleEditSave = async () => {
    if (!householdId || !editingMember) return;

    setIsSavingEdit(true);
    setEditError('');

    try {
      const updates: Record<string, string> = {};

      if (editDisplayName.trim() !== (editingMember.displayName || '')) {
        updates.displayName = editDisplayName.trim();
      }
      if (editFirstName.trim() !== (editingMember.firstName || '')) {
        updates.firstName = editFirstName.trim();
      }
      if (editLastName.trim() !== (editingMember.lastName || '')) {
        updates.lastName = editLastName.trim();
      }
      if (editRole !== editingMember.role) {
        updates.role = editRole;
      }

      if (Object.keys(updates).length === 0) {
        closeEdit();
        return;
      }

      await put(
        `/v1/households/${householdId}/members/${editingMember.userId}`,
        updates,
      );

      await fetchMembers();
      closeEdit();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to update member';
      setEditError(msg);
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Can the current user edit this member?
  const canEdit = (member: HouseholdMember): boolean => {
    if (!isPrimary) return false;
    // Account holder can only be edited by themselves
    if (member.isAccountHolder && member.userId !== user?.userId) return false;
    return true;
  };

  // Can the role be changed for a member being edited?
  const canChangeRole = (member: HouseholdMember): boolean => {
    // Account holder's role is locked
    if (member.isAccountHolder) return false;
    // Cannot change your own role
    if (member.userId === user?.userId) return false;
    return true;
  };

  // Auto-clear status message
  useEffect(() => {
    if (inviteStatus !== 'idle') {
      const timer = setTimeout(() => {
        setInviteStatus('idle');
        setInviteMessage('');
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [inviteStatus]);

  return (
    <div className="page-container">
      <h1 className="page-title">Tribe Members</h1>

      {/* ---- Current Members ---- */}
      <div className="users-card">
        <h3>Current Members</h3>

        {isLoadingMembers ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--color-text-muted)' }}>
            <Loader size={20} className="spin" /> Loading members...
          </div>
        ) : membersError ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--color-error, #DC2626)' }}>
            <AlertCircle size={18} style={{ marginRight: 6, verticalAlign: 'middle' }} />
            {membersError}
          </div>
        ) : (
          <div className="users-list">
            {members.map((m) => (
              <div key={m.userId} className="user-row">
                <div className="user-avatar">{m.displayName.charAt(0)}</div>
                <div className="user-info">
                  <span className="user-name">
                    {m.displayName}
                    {m.userId === user?.userId && (
                      <span style={{ color: 'var(--color-text-muted)', fontWeight: 400, fontSize: 13 }}> (you)</span>
                    )}
                    {m.isAccountHolder && <span className="user-badge user-badge--owner">Account Holder</span>}
                  </span>
                  <span className="user-email">{m.email}</span>
                </div>
                <div className="user-role-control">
                  {m.role === 'primary' ? (
                    <span className="user-badge user-badge--primary"><ShieldCheck size={12} /> Primary</span>
                  ) : (
                    <span className="user-badge user-badge--member"><Shield size={12} /> Member</span>
                  )}
                </div>
                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  {canEdit(m) && (
                    <button
                      className="user-action-btn"
                      title="Edit member"
                      onClick={() => openEdit(m)}
                    >
                      <Pencil size={14} />
                    </button>
                  )}
                  {isPrimary && !m.isAccountHolder && m.userId !== user?.userId && (
                    <button
                      className="user-action-btn"
                      title="Remove member"
                      onClick={() => handleRemove(m)}
                      disabled={removingUserId === m.userId}
                    >
                      {removingUserId === m.userId ? <Loader size={14} className="spin" /> : <Trash2 size={14} />}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {members.length === 0 && (
              <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: '16px 0' }}>
                No members yet. Invite your tribe below!
              </p>
            )}
          </div>
        )}
      </div>

      {/* ---- Edit Member Modal ---- */}
      {editingMember && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000,
            padding: 16,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) closeEdit(); }}
        >
          <div style={{
            background: 'var(--color-surface, white)', borderRadius: 12,
            padding: 24, width: '100%', maxWidth: 440,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0 }}>Edit Member</h3>
              <button
                onClick={closeEdit}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--color-text-muted)' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Email (read-only) */}
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Email</label>
              <input
                className="form-input"
                value={editingMember.email}
                disabled
                style={{ opacity: 0.6, cursor: 'not-allowed' }}
              />
              <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--color-text-muted)' }}>
                Email cannot be changed after account creation.
              </p>
            </div>

            {/* Display Name */}
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Display Name</label>
              <input
                className="form-input"
                value={editDisplayName}
                onChange={(e) => setEditDisplayName(e.target.value)}
                placeholder="Display name"
                disabled={isSavingEdit}
              />
            </div>

            {/* First / Last Name row */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">First Name</label>
                <input
                  className="form-input"
                  value={editFirstName}
                  onChange={(e) => setEditFirstName(e.target.value)}
                  placeholder="First name"
                  disabled={isSavingEdit}
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Last Name</label>
                <input
                  className="form-input"
                  value={editLastName}
                  onChange={(e) => setEditLastName(e.target.value)}
                  placeholder="Last name"
                  disabled={isSavingEdit}
                />
              </div>
            </div>

            {/* Role */}
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label">Role</label>
              <select
                className="form-input form-select"
                value={editRole}
                onChange={(e) => setEditRole(e.target.value as 'primary' | 'member')}
                disabled={isSavingEdit || !canChangeRole(editingMember)}
              >
                <option value="member">Member</option>
                <option value="primary">Primary</option>
              </select>
              {editingMember.isAccountHolder && (
                <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--color-text-muted)' }}>
                  The account holder must remain a primary user.
                </p>
              )}
              {!editingMember.isAccountHolder && editingMember.userId === user?.userId && (
                <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--color-text-muted)' }}>
                  You cannot change your own role.
                </p>
              )}
            </div>

            {/* Error */}
            {editError && (
              <div style={{
                color: 'var(--color-error, #DC2626)', fontSize: 14, marginBottom: 16,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <AlertCircle size={16} /> {editError}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={closeEdit}
                className="btn"
                style={{ background: 'var(--color-bg-alt, #f0f0f0)', color: 'var(--color-text)' }}
                disabled={isSavingEdit}
              >
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                className="btn btn-primary"
                disabled={isSavingEdit}
              >
                {isSavingEdit ? <><Loader size={16} className="spin" /> Saving...</> : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Invite Form (primary users only) ---- */}
      {isPrimary && (
        <div className="users-card">
          <h3><UserPlus size={18} /> Add a Member</h3>

          {/* Method toggle */}
          <div style={{
            display: 'flex', gap: 8, marginBottom: 20,
            background: 'var(--color-bg-alt, #F5F0E1)', borderRadius: 8, padding: 4,
          }}>
            <button
              type="button"
              onClick={() => setInviteMethod('email')}
              style={{
                flex: 1, padding: '10px 16px', borderRadius: 6, border: 'none',
                cursor: 'pointer', fontWeight: 600, fontSize: 14,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                background: inviteMethod === 'email' ? 'var(--color-surface, white)' : 'transparent',
                color: inviteMethod === 'email' ? 'var(--color-text)' : 'var(--color-text-muted)',
                boxShadow: inviteMethod === 'email' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}
            >
              <Mail size={16} /> Send Email Invite
            </button>
            <button
              type="button"
              onClick={() => setInviteMethod('manual')}
              style={{
                flex: 1, padding: '10px 16px', borderRadius: 6, border: 'none',
                cursor: 'pointer', fontWeight: 600, fontSize: 14,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                background: inviteMethod === 'manual' ? 'var(--color-surface, white)' : 'transparent',
                color: inviteMethod === 'manual' ? 'var(--color-text)' : 'var(--color-text-muted)',
                boxShadow: inviteMethod === 'manual' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}
            >
              <KeyRound size={16} /> Set Up with Password
            </button>
          </div>

          <form onSubmit={handleInvite} className="invite-form">
            {/* Name row */}
            <div className="invite-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">First Name</label>
                <input
                  className="form-input"
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  disabled={isInviting}
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Last Name</label>
                <input
                  className="form-input"
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  disabled={isInviting}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select
                  className="form-input form-select"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as 'primary' | 'member')}
                  disabled={isInviting}
                >
                  <option value="member">Member</option>
                  <option value="primary">Primary</option>
                </select>
              </div>
            </div>

            {/* Email + optional password row */}
            <div className="invite-row">
              <div className="form-group" style={{ flex: 1.5 }}>
                <label className="form-label">Email</label>
                <input
                  className="form-input"
                  type="email"
                  placeholder={inviteMethod === 'manual' ? 'their@email.com or yours+name@email.com' : 'their@email.com'}
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                  disabled={isInviting}
                />
                {inviteMethod === 'manual' && (
                  <p style={{
                    margin: '6px 0 0', fontSize: 12, color: 'var(--color-text-muted)',
                    display: 'flex', alignItems: 'flex-start', gap: 4,
                  }}>
                    <Info size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                    No email? Use yours with a + tag: <strong>you+theirname@gmail.com</strong>
                  </p>
                )}
              </div>
              {inviteMethod === 'manual' && (
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Temporary Password</label>
                  <input
                    className="form-input"
                    type="text"
                    placeholder="At least 8 characters"
                    value={tempPassword}
                    onChange={(e) => setTempPassword(e.target.value)}
                    required
                    minLength={8}
                    disabled={isInviting}
                  />
                  <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--color-text-muted)' }}>
                    Must include uppercase, lowercase, and a number. They'll change it on first login.
                  </p>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <button type="submit" className="btn btn-primary" disabled={isInviting}>
                {isInviting ? (
                  <><Loader size={16} className="spin" /> {inviteMethod === 'manual' ? 'Adding...' : 'Sending...'}</>
                ) : inviteMethod === 'manual' ? (
                  <><UserPlus size={16} /> Add Member</>
                ) : (
                  <><Send size={16} /> Send Invite</>
                )}
              </button>
              {inviteStatus === 'success' && (
                <span style={{ color: 'var(--color-success, #059669)', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <CheckCircle size={16} /> {inviteMessage}
                </span>
              )}
              {inviteStatus === 'error' && (
                <span style={{ color: 'var(--color-error, #DC2626)', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <AlertCircle size={16} /> {inviteMessage}
                </span>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
